import {
  MEMBER_ROLE_CAREGIVER,
  MEMBER_ROLE_PARENT_AND_CAREGIVER,
  MEMBER_ROLE_PARENT_NOT_CAREGIVER,
} from './constants';
import {
  CLIENT_SIBLING_FORM_SCHEMA,
  CULTURAL_CONSIDERATION_RICH_TEXT_SCHEMA,
  PARENT_CAREGIVER_FORM_SCHEMA,
} from './validationSchema';

import type {
  ClientSiblingFieldName,
  ClientSiblingFormValues,
  ClientSiblingPayload,
  FamilyMemberFieldName,
  FamilyMemberFormValues,
  FamilyMemberPayload,
  IClientSibling,
  IFamily,
  IFamilyMember,
} from './types';

export function mapFamilyMemberToValues(member: IFamilyMember): FamilyMemberFormValues {
  return {
    full_name: member.full_name ?? '',
    member_role: member.member_role ?? '',
    caregiver_type: member.caregiver_type ?? null,
    relationship_to_client: member.relationship_to_client ?? '',
    custody_type: member.custody_type ?? '',
    marital_status: member.marital_status ?? '',
    job_title: member.job_title ?? '',
    email: member.email ?? '',
    phone: member.phone ?? '',
    street_address_line_one: member.street_address_line_one ?? '',
    street_address_line_two: member.street_address_line_two ?? '',
    street_address_city: member.street_address_city ?? '',
    street_address_state: member.street_address_state ?? '',
    street_address_zip: member.street_address_zip ?? '',
  };
}

export function getFamilyMemberRowLabel(member: IFamilyMember): string {
  const name = member.full_name?.trim() || 'Parent / caregiver';
  const role = member.member_role?.trim();
  return role ? `${name} - ${role}` : name;
}

export function toFamilyMemberFieldPayload(
  name: FamilyMemberFieldName,
  values: FamilyMemberFormValues,
): FamilyMemberPayload {
  const data: FamilyMemberPayload = {};
  data[name] = values[name] || null;
  // A parent who is not a caregiver has no caregiver type, so clear it alongside the role change.
  if (name === 'member_role' && values.member_role === MEMBER_ROLE_PARENT_NOT_CAREGIVER) {
    data.caregiver_type = null;
  }
  return data;
}

export function toClientSiblingFieldPayload(
  name: ClientSiblingFieldName,
  values: ClientSiblingFormValues,
): ClientSiblingPayload {
  switch (name) {
    case 'sibling_age':
      return { sibling_age: values.sibling_age === '' ? null : Number(values.sibling_age) };
    case 'sibling_relationship':
      return { sibling_relationship: values.sibling_relationship || null };
    case 'sibling_diagnosis':
      return { sibling_diagnosis: values.sibling_diagnosis || null };
  }
}

export function mapClientSiblingToValues(row: IClientSibling): ClientSiblingFormValues {
  return {
    sibling_relationship: row.sibling_relationship ?? '',
    sibling_age: row.sibling_age != null ? String(row.sibling_age) : '',
    sibling_diagnosis: row.sibling_diagnosis ?? '',
  };
}

export function getClientSiblingRowLabel(row: IClientSibling): string {
  const relationship = row.sibling_relationship ?? '';
  return row.sibling_age != null ? `${relationship} - Age ${row.sibling_age}` : relationship;
}

// True when at least one member has a parent role ('Parent and a caregiver' or 'Parent not a caregiver').
export function hasFamilyParentRole(members: ReadonlyArray<IFamilyMember>): boolean {
  return members.some(
    (member) =>
      member.member_role === MEMBER_ROLE_PARENT_AND_CAREGIVER ||
      member.member_role === MEMBER_ROLE_PARENT_NOT_CAREGIVER,
  );
}

// True when at least one member has a caregiver role ('Parent and a caregiver' or 'Caregiver').
export function hasFamilyCaregiverRole(members: ReadonlyArray<IFamilyMember>): boolean {
  return members.some(
    (member) =>
      member.member_role === MEMBER_ROLE_PARENT_AND_CAREGIVER ||
      member.member_role === MEMBER_ROLE_CAREGIVER,
  );
}

// FA-3463: the tab needs at least one parent role and at least one caregiver role across all members.
export function getFamilyRoleCoverageError(
  members: ReadonlyArray<IFamilyMember>,
): string | undefined {
  if (members.length === 0) {
    return undefined;
  }

  const hasParentRole = hasFamilyParentRole(members);
  const hasCaregiverRole = hasFamilyCaregiverRole(members);

  if (!hasParentRole && !hasCaregiverRole) {
    return 'Add at least one parent and at least one caregiver. One person with the role "Parent and a caregiver" covers both.';
  }
  if (!hasParentRole) {
    return 'Add at least one parent ("Parent and a caregiver" or "Parent not a caregiver").';
  }
  if (!hasCaregiverRole) {
    return 'Add at least one caregiver ("Parent and a caregiver" or "Caregiver").';
  }
  return undefined;
}

// True once the family tab has at least one saved field — a cultural consideration, a parent /
// caregiver, a sibling, or any medical-history / support entry. Gates the tab's error icon so a
// family the user never touched stays neutral instead of erroring on its "required" lists.
export function hasFamilyFieldValue(family: IFamily | null | undefined): boolean {
  if (!family) {
    return false;
  }

  return (
    Boolean(family.cultural_consideration) ||
    family.family_members.length > 0 ||
    family.client_siblings.length > 0 ||
    family.maternal_medical_history.length > 0 ||
    family.paternal_medical_history.length > 0 ||
    family.community_support.length > 0
  );
}

// True when the family tab has a validation error: no parent / caregiver has been added (at least
// one is required), the members do not cover both a parent role and a caregiver role, any saved
// parent / caregiver or sibling row fails validation, or a required medical-history / support list
// is empty, or cultural considerations exceed the length limit. Mirrors the list UI — the required
// message / error icon on the add buttons and the per-row error icons (all driven by the same
// schemas) — so the family tab flags exactly what the lists show, and clears the moment those
// errors are resolved.
export function hasFamilyFieldErrors(family: IFamily | null | undefined): boolean {
  if (!family) {
    return false;
  }

  const hasNoFamilyMembers = family.family_members.length === 0;

  const hasInvalidFamilyMember = family.family_members.some(
    (member) =>
      !PARENT_CAREGIVER_FORM_SCHEMA.isValidSync(mapFamilyMemberToValues(member), {
        abortEarly: false,
      }),
  );

  const hasInvalidSibling = family.client_siblings.some(
    (sibling) =>
      !CLIENT_SIBLING_FORM_SCHEMA.isValidSync(mapClientSiblingToValues(sibling), {
        abortEarly: false,
      }),
  );

  const hasMissingRequiredRoles =
    !hasFamilyParentRole(family.family_members) || !hasFamilyCaregiverRole(family.family_members);

  const hasMissingRequiredAdditionalInfo =
    family.maternal_medical_history.length === 0 ||
    family.paternal_medical_history.length === 0 ||
    family.community_support.length === 0;

  return (
    hasNoFamilyMembers ||
    hasMissingRequiredRoles ||
    hasInvalidFamilyMember ||
    hasInvalidSibling ||
    hasMissingRequiredAdditionalInfo ||
    !hasValidCulturalConsideration(family.cultural_consideration)
  );
}

// The family tab is complete when it has at least one valid parent / caregiver, the members cover
// both a parent role and a caregiver role, every sibling row passes validation, and each required
// medical-history / support list has an entry, and cultural considerations are within the length
// limit. Drives the green success check; the error icon is driven by `hasFamilyFieldErrors`.
export function isFamilyComplete(family: IFamily | null | undefined): boolean {
  if (!family) {
    return false;
  }

  const hasValidFamilyMembers =
    family.family_members.length > 0 &&
    family.family_members.every((member) =>
      PARENT_CAREGIVER_FORM_SCHEMA.isValidSync(mapFamilyMemberToValues(member), {
        abortEarly: false,
      }),
    );

  const hasValidSiblings = family.client_siblings.every((sibling) =>
    CLIENT_SIBLING_FORM_SCHEMA.isValidSync(mapClientSiblingToValues(sibling), { abortEarly: false }),
  );

  const hasRequiredRoles =
    hasFamilyParentRole(family.family_members) && hasFamilyCaregiverRole(family.family_members);

  const hasRequiredMedicalHistory =
    family.maternal_medical_history.length > 0 &&
    family.paternal_medical_history.length > 0 &&
    family.community_support.length > 0;

  return (
    hasValidFamilyMembers &&
    hasRequiredRoles &&
    hasValidSiblings &&
    hasRequiredMedicalHistory &&
    hasValidCulturalConsideration(family.cultural_consideration)
  );
}

function hasValidCulturalConsideration(value: string | null): boolean {
  return CULTURAL_CONSIDERATION_RICH_TEXT_SCHEMA.isValidSync(
    { cultural_consideration: value ?? '' },
    { abortEarly: false },
  );
}
